import { Component, computed, inject, signal } from '@angular/core';
import { IncometaxService, TaxBatchJob, TaxStartRequest } from '../Model/Incometax/incometax.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ComboService } from '../Model/Combo/combo.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { SessionService } from '../Util/Session/session.service';
import { LoaderComponent } from '../Component/Loader/loader/loader.component';

@Component({
  selector: 'app-tax-monitor',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, LoaderComponent],
  templateUrl: './tax-monitor.component.html',
  styleUrl: './tax-monitor.component.scss'
})
export class TaxMonitorComponent {
  private taxService = inject(IncometaxService);
  private comboload = inject(ComboService);
  private session = inject(SessionService);
  private pollingSub?: Subscription;
  
  constructor(){
    this.formData.initiatedBy = this.session.GetUserDetails().UserId;
  }

  // Expose Math to template for pagination
  protected readonly Math = Math;

  // Tabs: 'live' or 'history'
  activeTab = signal<'live' | 'history'>('live');

  // Live monitor state
  activeJob = signal<TaxBatchJob | null>(null);
  isSubmitting = signal<boolean>(false);

  // Strict boolean for [disabled] bindings (Never returns null!)
  isJobRunning = computed<boolean>(() => this.activeJob()?.status === 'RUNNING');

  // History state
  batchList = signal<any[]>([]);
  isLoadingHistory = signal<boolean>(false);

  // Selected batch for logs modal
  selectedBatch = signal<any | null>(null);
  batchLogs = signal<any[]>([]);
  logsTotalCount = signal<number>(0);
  logStatusFilter = signal<any>('');
  logSearchQuery = signal<any>('');
  logPage = signal<any>(1);
  isLoadingLogs = signal<boolean>(false);

  // Computed Pagination
  totalPages = computed<number>(() => {
    const total = this.logsTotalCount();
    return total > 0 ? Math.ceil(total / 50) : 1;
  });

  endRecord = computed<number>(() => {
    return Math.min(this.logPage() * 50, this.logsTotalCount());
  });

  // Form input model (Includes single empCode filter)
  formData: TaxStartRequest = {
    empCode: '',
    financialYear: null as any,
    branchID: null,
    clientID: null,
    siteID: null,
    empType: '',
    empServiceTypeID: 0,
    isFilterBasedonGross: false,
    filterGrossValues: 0,
    initiatedBy: ''
  };

  // Progress percentage
  progressPercent = computed<number>(() => {
    const job = this.activeJob();
    if (!job || job.totalEmployees === 0) return 0;
    return Math.min(100, Math.round((job.processedCount / job.totalEmployees) * 100));
  });

  ngOnInit(): void {
    // 1. Check if a job is actively running in background on server
    this.taxService.checkRunningJob().subscribe({
      next: (res) => {
        this.listenToJob('');
      },
      error: () => {
        this.listenToJob('');
      }
    });

    // 2. Preload history
    this.loadBatchHistory();
    this.ComboLoad('Branch');
    this.ESS_TaxFinancialYearSelect();
  }

  isDisabled: boolean = false;
  user = [] as any;
  Branchs = [];
  Clients = [];
  Sites = [];
  TableDatas = [] as any;

  Filter = {
    Date: '' as any,
    BranchId: null,
    ClientId: null,
    SiteId: null,
    EmpCode: '' as any
  }

  ComboLoad(Combotype: String) {
    this.isDisabled = true;
    if (Combotype == 'Site') {
      this.Filter.SiteId = null;
      this.Sites = [];
    } else if (Combotype == 'Client') {
      this.Filter.ClientId = null;
      this.Filter.SiteId = null;
      this.Clients = [];
      this.Sites = [];
    }
    this.comboload.ComboLoad({
      'Combotype': Combotype,
      'UserType': 'A',
      'BranchId': this.Filter.BranchId,
      'ClientId': this.Filter.ClientId,
      'SiteId': this.Filter.SiteId,
      'EmpType': 'front',
      'EmpCode': this.Filter.EmpCode
    }).subscribe({
      next: (response) => {
        if (response.ComboLoad) {
          if (Combotype == 'Branch') {
            this.Branchs = response.ComboLoad;
          } else if (Combotype == 'Client') {
            this.Clients = response.ComboLoad;
          } else if (Combotype == 'Site') {
            this.Sites = response.ComboLoad;
          } else if (Combotype == 'EmpLoadForRegularization') {
            if (this.Filter.BranchId == null) {
              // this.toast.show({ message: 'Kindly select atleast Branch', type: toastTypeEnum.warning });
              this.isDisabled = false;
              return;
            }
            if (response.ComboLoad.length == 0) {
              // this.toast.show({ message: 'No data found', type: toastTypeEnum.warning });
              this.isDisabled = false;
              return;
            }
            this.TableDatas = response.ComboLoad;
          }
        }
        this.isDisabled = false;
      },
      complete: () => {
        this.isDisabled = false;
      },
      error: (error) => {
        this.isDisabled = false;
      },
    });
  }

  // Start process on server
  startProcess(): void {

    if(this.formData.financialYear == null || this.formData.financialYear == ''){
      alert("Select Financial Year");
      return;
    }

    if (this.isJobRunning()) {
      alert(`A batch (${this.activeJob()?.id}) is already RUNNING! Please wait for it to complete or stop it.`);
      return;
    }

    this.isSubmitting.set(true);

    this.taxService.startJob(this.formData).subscribe({
      next: (res) => {
        this.isSubmitting.set(false);
        // this.taxService.saveActiveJobId(res.jobId);
        this.listenToJob(res.jobId);
        this.activeTab.set('live');
        this.loadBatchHistory();
      },
      error: (err) => {
        this.isSubmitting.set(false);
        if (err.status === 409 && err.error?.runningJobId) {
          alert(`⚠️ ${err.error.message}`);
          this.listenToJob(err.error.runningJobId);
        } else {
          alert(err.error?.message || 'Failed to trigger process. Please check API server.');
        }
      }
    });
  }

  // Manual cancel / stop batch
  cancelCurrentBatch(): void {
    const job = this.activeJob();
    if (!job) return;

    if (!confirm(`Are you sure you want to stop Batch ${job.id}? Any employees already processed will remain saved.`)) {
      return;
    }

    this.taxService.cancelJob(job.id).subscribe({
      next: () => {
        alert(`Batch ${job.id} has been cancelled.`);
        this.pollingSub?.unsubscribe();
        // this.taxService.clearActiveJobId();
        this.resetView();
        this.loadBatchHistory();
      },
      error: () => alert('Failed to cancel batch.')
    });
  }

  // Reprocess a single employee
  reprocessEmp(emp: any): void {
    const batch = this.selectedBatch() || this.activeJob();
    const jobGuid = batch?.jobGuid || batch?.JobGuid || batch?.id;
    if (!jobGuid) return;

    const empIdentifier = emp.empCode || emp.employeeId;
    if (!confirm(`Are you sure you want to recalculate tax for Employee ${empIdentifier} (${emp.employeeName})?`)) {
      return;
    }

    this.taxService.reprocessEmployee(jobGuid, emp.employeeId, emp.empCode || '').subscribe({
      next: (res) => {
        if (res.status === 'SUCCESS') {
          alert(`✓ Employee ${empIdentifier} recalculated successfully!`);
        } else {
          alert(`✕ Employee ${empIdentifier} failed: ${res.errorMessage}`);
        }
        this.fetchLogs();
      },
      error: () => alert('Failed to reprocess employee. Please check API.')
    });
  }

  wakeUpDaemon(){
    this.taxService.wakeUpDaemon().subscribe({
      next: (res) => {},
      error: () => alert('Failed to wake up daemon. Please check API.')
    });
  }

  // Poll live status
  // private listenToJob(jobId: string): void {
  //   this.pollingSub?.unsubscribe();

  //   this.pollingSub = this.taxService.pollJobStatus$(jobId).subscribe({
  //     next: (res) => {
  //       console.log(res.job);
  //       this.activeJob.set(res.job);
  //       console.log(this.activeJob);
  //       if (res.job.status === 'COMPLETED' || res.job.status === 'FAILED' || res.job.status === 'CANCELLED') {
  //         this.taxService.clearActiveJobId();
  //         this.loadBatchHistory();
  //       }
  //     },
  //     error: (err) => console.warn('Polling error, retrying...', err)
  //   });
  // }

  private listenToJob(jobId: string): void {
    this.pollingSub?.unsubscribe();
    
    this.pollingSub = this.taxService.pollJobStatus$(jobId).subscribe({
      next: (res) => {
        if(res.job.id !== ""){
          this.activeJob.set(res.job);
        }

        if (
          res.job.status === 'COMPLETED' ||
          res.job.status === 'FAILED' ||
          res.job.status === 'CANCELLED'
        ) {
          this.loadBatchHistory();
        }
      },
      error: (err) => {
      console.error('HTTP Status:', err.status);
      console.error('Request URL:', err.url);
      console.error('Response body:', err.error);
      console.error('Error message:', err.message);
      }
    });
  }

  checkJobStatus(): void {
    // const jobId = this.taxService.getActiveJobId();
    
    // if (jobId) {
      this.listenToJob('');
    // }
  }

  // Load past batches
  loadBatchHistory(): void {
    this.isLoadingHistory.set(true);
    this.taxService.getAllBatches().subscribe({
      next: (res) => {
        this.batchList.set(res.batches || []);
        this.isLoadingHistory.set(false);
      },
      error: () => this.isLoadingHistory.set(false)
    });
  }

  // Open log modal for a batch
  viewBatchLogs(batch: any): void {
    this.selectedBatch.set(batch);
    this.logPage.set(1);
    this.logStatusFilter.set('');
    this.logSearchQuery.set('');
    this.fetchLogs();
  }

  fetchLogs(): void {
    const batch = this.selectedBatch();
    if (!batch) return;

    const jobGuid = batch.jobGuid || batch.JobGuid || batch.id;
    this.isLoadingLogs.set(true);
    this.taxService.getBatchLogs(
      jobGuid,
      this.logPage(),
      50,
      this.logStatusFilter(),
      this.logSearchQuery()
    ).subscribe({
      next: (res) => {
        this.batchLogs.set(res.logs || []);
        this.logsTotalCount.set(res.totalCount || 0);
        this.isLoadingLogs.set(false);
      },
      error: () => this.isLoadingLogs.set(false)
    });
  }

  // Pagination navigation
  goToFirstPage(): void {
    if (this.logPage() !== 1) {
      this.logPage.set(1);
      this.fetchLogs();
    }
  }

  goToNextPage(): void {
    if (this.logPage() < this.totalPages()) {
      this.logPage.update(p => p + 1);
      this.fetchLogs();
    }
  }

  goToPrevPage(): void {
    if (this.logPage() > 1) {
      this.logPage.update(p => p - 1);
      this.fetchLogs();
    }
  }

  closeLogsModal(): void {
    this.selectedBatch.set(null);
    this.batchLogs.set([]);
  }

  resetView(): void {
    this.pollingSub?.unsubscribe();
    // this.taxService.clearActiveJobId();
    this.activeJob.set(null);
  }

  FinancialYears: any[] = [];
  ESS_TaxFinancialYearSelect() {
    this.isDisabled = true;
    this.taxService.ESS_TaxFinancialYearSelect({ SelectType: 'S', ESS_TaxFinancialYearID: null, FinancialYearFrom: null, FinancialYearTo: null }).subscribe({
      next: (response) => {
        if (response.Table) {
          this.FinancialYears = response.Table;
          this.FinancialYears.forEach(element => {
            element.FinancialYear = new Date(element.FinancialYearStart).getFullYear() + '-' + new Date(element.FinancialYearEnd).getFullYear();
          });
        } else {
          this.FinancialYears = [];
        }
        this.isDisabled = false;
      },
      complete: () => { },
      error: (error) => {
        this.isDisabled = false;
      },
    })
  };
}
